import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://bamabang.net";
const SUPPORTED_LANGS = ["en", "zh", "ja", "es", "de", "fr", "pt", "ru"];

interface SeoHeadProps {
  /** Page path without leading slash for canonical, e.g. "privacy", "" for homepage */
  path: string;
  /** Page title — will be used as-is (no suffix appended) */
  title: string;
  /** Meta description */
  description: string;
  /** Override OG image URL (defaults to the standard og-image) */
  ogImage?: string;
  /** Override og:type (defaults to "website") */
  ogType?: string;
  /** Additional children to inject inside <Helmet> (e.g. JSON-LD scripts) */
  children?: React.ReactNode;
}

/**
 * Unified SEO <head> component.
 *
 * Injects: canonical URL, Open Graph, Twitter Cards, hreflang alternates.
 * Drop this into every public page to guarantee consistent SEO coverage.
 */
export default function SeoHead({
  path,
  title,
  description,
  ogImage,
  ogType = "website",
  children,
}: SeoHeadProps) {
  const { i18n } = useTranslation();

  const canonicalUrl = `${SITE_URL}/${path}`;
  const imageUrl = ogImage || `${SITE_URL}/og-image.svg`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="BamaBang" />
      <meta property="og:locale" content={i18n.language} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* hreflang alternates — tell Google about all language versions */}
      {SUPPORTED_LANGS.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${SITE_URL}/${path}${path ? "?" : "?"}lang=${lang}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {children}
    </Helmet>
  );
}
